import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  RecurringInvoiceQc,
  PassQcOutput,
  ManualExecutionQcOutput,
  ErrorQcOutput,
  InvoiceDeliveryChannels,
} from "models/recurring-invoice-qc";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StatusChip from "../general/StatusChip";
import { getNetSuiteLink } from "src/util/helpers";
import { useRecurringInvoiceQcRequest } from "src/hooks/recurring-invoice-qc/useRecurringInvoiceQcRequest";

const RecurringInvoiceQC: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  const {
    recurringInvoiceQcRequest: qcRecord,
    loading,
    fetchRecurringInvoiceQcRequestById,
  } = useRecurringInvoiceQcRequest();

  const fetchQcRecord = useCallback(async () => {
    if (id) {
      const lookupId = id.split("-")[1]; // Extract the ID part from 'RECINVQC-ANBRSU'
      setRefreshing(true);

      try {
        await fetchRecurringInvoiceQcRequestById(lookupId);
      } finally {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchQcRecord();
  }, []);

  useEffect(() => {
    if (qcRecord?.status === "IN_PROGRESS") {
      const intervalId = setInterval(() => {
        fetchQcRecord();
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [qcRecord?.status]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!qcRecord) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="body1">
          No Recurring Invoice QC record found.
        </Typography>
      </Box>
    );
  }

  const renderDeliveryChannels = (output: InvoiceDeliveryChannels) => (
    <>
      {output.pdfSendError && (
        <Typography variant="body2">
          <strong>PDF Send Error:</strong> {output.pdfSendError}
        </Typography>
      )}
      {output.vertexApplicable && (
        <>
          <Typography variant="body2">
            <strong>Vertex Applicable:</strong> Yes
          </Typography>
          <Typography variant="body2">
            <strong>Vertex Attempted:</strong>{" "}
            {output.vertexAttempted ? "Yes" : "No"}
          </Typography>
          <Typography variant="body2">
            <strong>Vertex Sent:</strong> {output.vertexSent ? "Yes" : "No"}
          </Typography>
          {output.vertexMessageId && (
            <Typography variant="body2">
              <strong>Vertex Message ID:</strong> {output.vertexMessageId}
            </Typography>
          )}
          {output.vertexStatus && (
            <Typography variant="body2">
              <strong>Vertex Status:</strong> {output.vertexStatus}
            </Typography>
          )}
          {output.vertexError && (
            <Typography variant="body2">
              <strong>Vertex Error:</strong> {output.vertexError}
            </Typography>
          )}
        </>
      )}
    </>
  );

  const renderOutputDetails = () => {
    if (qcRecord?.output.decision === "PASS") {
      const output = qcRecord.output as PassQcOutput;
      return (
        <>
          <Typography variant="body2">
            <strong>Already Sent:</strong> {output.alreadySent ? "Yes" : "No"}
          </Typography>
          <Typography variant="body2">
            <strong>PDF Was Sent:</strong> {output.wasSent ? "Yes" : "No"}
          </Typography>
          {output.toList && output.toList.length > 0 && (
            <Typography variant="body2">
              <strong>To List:</strong> {output.toList.join(", ")}
            </Typography>
          )}
          {output.ccList && output.ccList.length > 0 && (
            <Typography variant="body2">
              <strong>CC List:</strong> {output.ccList.join(", ")}
            </Typography>
          )}
          {renderDeliveryChannels(output)}
          <Typography variant="body2">
            <strong>Needs Upload:</strong> {output.needsUpload ? "Yes" : "No"}
          </Typography>
          {output.uploadWorkUnit && (
            <Typography variant="body2">
              <strong>Upload Work Unit:</strong>{" "}
              <a
                href={`https://devgraph-alp.atlassian.net/browse/${output.uploadWorkUnit}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {output.uploadWorkUnit}
              </a>
            </Typography>
          )}
        </>
      );
    } else if (
      qcRecord?.output &&
      'reasoning' in qcRecord.output &&
      'workUnit' in qcRecord.output
    ) {
      const output = qcRecord.output as ManualExecutionQcOutput | ErrorQcOutput;
      return (
        <>
          <Typography variant="body2">
            <strong>Reasoning:</strong> {output.reasoning}
          </Typography>
          <Typography variant="body2">
            <strong>Work Unit:</strong>{" "}
            <a
              href={`https://devgraph-alp.atlassian.net/browse/${output.workUnit}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {output.workUnit}
            </a>
          </Typography>
          {output.wasSent !== undefined && (
            <Typography variant="body2">
              <strong>PDF Was Sent:</strong> {output.wasSent ? "Yes" : "No"}
            </Typography>
          )}
          {output.toList && output.toList.length > 0 && (
            <Typography variant="body2">
              <strong>To List:</strong> {output.toList.join(", ")}
            </Typography>
          )}
          {output.ccList && output.ccList.length > 0 && (
            <Typography variant="body2">
              <strong>CC List:</strong> {output.ccList.join(", ")}
            </Typography>
          )}
          {renderDeliveryChannels(output)}
        </>
      );
    }
    return null;
  };

  return (
    <Box p={3}>
      {qcRecord ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3} gap={2}>
            <IconButton
              onClick={() => navigate("/recurring-invoice-qc")}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">RECINVQC-{qcRecord.requestId}</Typography>
            <Chip
              label={`Invoice ID: ${
                qcRecord.input.tranId || qcRecord.input.invoiceId
              }`}
              color="default"
            />
            <StatusChip status={qcRecord.status} />
            <Chip
              label={qcRecord.output.decision}
              color={
                qcRecord.output.decision === "PASS"
                  ? "success"
                  : qcRecord.output.decision === "ERROR"
                  ? "error"
                  : "warning"
              }
            />
            {refreshing && <CircularProgress size={24} />}
          </Box>
          <Box mb={3}>
            <Typography variant="h6">Input Details</Typography>
            <Typography variant="body2">
              <strong>Invoice ID:</strong>{" "}
              <a
                href={getNetSuiteLink("Invoice", qcRecord.input.invoiceId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {qcRecord.input.invoiceId}
              </a>
            </Typography>
            {qcRecord.input.tranId && (
              <Typography variant="body2">
                <strong>Transaction ID:</strong> {qcRecord.input.tranId}
              </Typography>
            )}
          </Box>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Output Details
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                <strong>Decision:</strong>
              </Typography>
              <Chip
                label={qcRecord.output.decision}
                color={
                  qcRecord.output.decision === "PASS"
                    ? "success"
                    : qcRecord.output.decision === "ERROR"
                    ? "error"
                    : "warning"
                }
              />
            </Box>
            <Box mt={2}>{renderOutputDetails()}</Box>
          </Box>
        </Paper>
      ) : (
        <Typography variant="body1">
          No Recurring Invoice QC record found.
        </Typography>
      )}
    </Box>
  );
};

export default RecurringInvoiceQC;
